import { Button, makeStyles, Typography } from '@material-ui/core';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { notify } from 'components/Notifier';
import CollapsableCard from 'components/Generic/CollapsableCard';
import { AppContext } from 'context';
import { MonthYearString } from 'types/MonthYearString';
import { getMonthAndYearStringsBetweenTwoDates, getMonthDiff } from 'utils/date';
import { useGetLatestStockModificationDate } from 'hooks';
import InventoryForm from 'components/Forms/InventoryForm';
import { ValidateIcon } from 'components/Icons';
import { useLatestClosure } from 'utils/closureHooks';
import { postInventoriedOutboundStocks } from 'services/apiOperations';
import { formatInventoriedOutboundStockBeforePost } from 'utils/normalizer';
import { InventoriedOutboundStockBatch, InventoryFormInterface, UserRole } from 'types';
import { ColorTypeEnum } from 'theme';
import { createConditionedStockCollapseMetaDataListIndex } from './utils';
import { ConditionedStockCollapseMetaDataList } from './types';

const useStyles = makeStyles(theme => ({
  typography: {
    display: 'flex',
    width: '100%',
  },
  element: {
    'text-transform': 'capitalize',
    width: '25%',
  },
  updateButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  actionButtonContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  actionButton: {
    margin: '1%',
  },
}));

const ConditionedStock: React.FC = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const context = useContext(AppContext);
  const [monthYearStrings, setMonthYearStrings] = useState<MonthYearString[]>([]);
  const [conditionedStockCollapseMetaDataList, setConditionedStockCollapseMetaDataList] =
    useState<ConditionedStockCollapseMetaDataList>({});
  const sortingCenter = context.getSortingCenter?.();
  const sortingCenterInitializationDate = sortingCenter ? sortingCenter.initializationDate : null;

  const roles = context.getRoles?.();
  const canEdit = sortingCenter?.isActive && !(roles && roles.includes(UserRole.ROLE_CONSULTATION));

  const endDate = useGetLatestStockModificationDate();
  const [latestClosure] = useLatestClosure();

  useEffect(() => {
    if (!sortingCenterInitializationDate || !endDate) return;

    const newMonthYearStrings: MonthYearString[] = getMonthAndYearStringsBetweenTwoDates(
      sortingCenterInitializationDate,
      endDate,
      false,
    );

    const latestClosureDate = latestClosure
      ? new Date(latestClosure.year, latestClosure.month, 0)
      : new Date(sortingCenterInitializationDate);

    const newConditionedStockCollapseMetaDataList: ConditionedStockCollapseMetaDataList =
      newMonthYearStrings.reduce<ConditionedStockCollapseMetaDataList>(
        (newConditionedStockCollapseMetaDataList, monthYearString: MonthYearString) => {
          const index = createConditionedStockCollapseMetaDataListIndex(monthYearString);
          const currentDate = new Date(parseInt(monthYearString.year), parseInt(monthYearString.month), 0);
          const isBeforeOrSameMonthThanLastClosure = currentDate <= latestClosureDate;
          const isTwoMonthsLaterDate = getMonthDiff(latestClosureDate, currentDate) > 2;

          newConditionedStockCollapseMetaDataList[index] = {
            isUpdateMode: false,
            isBeforeOrSameMonthThanLastClosure,
            resetForm: false,
            resetFormValues: [],
            isTwoMonthsLaterDate,
            color: isBeforeOrSameMonthThanLastClosure ? ColorTypeEnum.PRIMARY : ColorTypeEnum.WARNING,
          };

          return newConditionedStockCollapseMetaDataList;
        },
        {},
      );

    setMonthYearStrings(newMonthYearStrings);
    setConditionedStockCollapseMetaDataList(newConditionedStockCollapseMetaDataList);
  }, [sortingCenterInitializationDate, endDate, latestClosure]);

  const handleClickUpdateButton = (index: string) => {
    setConditionedStockCollapseMetaDataList(previousList => ({
      ...previousList,
      [index]: {
        ...previousList[index],
        isUpdateMode: true,
        resetForm: false,
      },
    }));
  };

  const handleClickCancelButton = (index: string) => {
    setConditionedStockCollapseMetaDataList(previousList => ({
      ...previousList,
      [index]: {
        ...previousList[index],
        isUpdateMode: false,
        resetForm: true,
      },
    }));
  };

  const handleSubmitInventoryForm = async (inventoryForm: InventoryFormInterface) => {
    if (!sortingCenter) return;

    const formattedInvetoriedOutboundSotcks = formatInventoriedOutboundStockBeforePost(
      inventoryForm,
      sortingCenter['@id'],
    );

    postInventoriedOutboundStocks(formattedInvetoriedOutboundSotcks)
      .then((data: InventoriedOutboundStockBatch) => {
        notify({
          message: t('conditionedStockPage.inventorySave'),
          severity: 'success',
        });

        inventoryForm.conditionedStocks = inventoryForm.conditionedStocks.map(stock => {
          const newData = data.inventoriedOutboundStocks.find(
            data =>
              String(data.month) === stock.inventoryMonth &&
              String(data.year) === stock.inventoryYear &&
              String(data.material) === stock.material,
          );
          return newData ? { ...stock, inventoryIdApiPlatform: newData['@id'] } : stock;
        });

        setConditionedStockCollapseMetaDataList(previousList => {
          const newList: ConditionedStockCollapseMetaDataList = {};
          const keyOfCurrentMonthUpdated = createConditionedStockCollapseMetaDataListIndex({
            month: (inventoryForm.conditionedStocks[0]?.inventoryMonth ?? '').toString(),
            year: (inventoryForm.conditionedStocks[0]?.inventoryYear ?? '').toString(),
          });

          for (const [key, conditionedStockCollapseMetaData] of Object.entries(previousList)) {
            if (key === keyOfCurrentMonthUpdated) {
              newList[key] = {
                ...conditionedStockCollapseMetaData,
                isUpdateMode: false,
                resetFormValues: inventoryForm.conditionedStocks,
                resetForm: true,
              };
            } else {
              newList[key] = {
                ...conditionedStockCollapseMetaData,
              };
            }
          }
          return newList;
        });
      })
      .catch(() => {
        notify({
          message: t('errorGeneric'),
          severity: 'error',
        });
      });
  };

  return (
    <>
      {monthYearStrings.map((monthYearString: MonthYearString) => (
        <CollapsableCard
          color={
            conditionedStockCollapseMetaDataList[createConditionedStockCollapseMetaDataListIndex(monthYearString)].color
          }
          key={monthYearString.monthInLetter + monthYearString.year}
          collapsableContent={
            <>
              <InventoryForm
                formName={createConditionedStockCollapseMetaDataListIndex(monthYearString)}
                onSubmit={handleSubmitInventoryForm}
                inventoryEditable={
                  conditionedStockCollapseMetaDataList[createConditionedStockCollapseMetaDataListIndex(monthYearString)]
                    .isUpdateMode
                }
                resetForm={
                  conditionedStockCollapseMetaDataList[createConditionedStockCollapseMetaDataListIndex(monthYearString)]
                    .resetForm
                }
                resetFormValues={
                  conditionedStockCollapseMetaDataList[createConditionedStockCollapseMetaDataListIndex(monthYearString)]
                    .resetFormValues
                }
                year={monthYearString.year}
                month={monthYearString.month}
                isClosedMonth={
                  conditionedStockCollapseMetaDataList[createConditionedStockCollapseMetaDataListIndex(monthYearString)]
                    .isBeforeOrSameMonthThanLastClosure
                }
              />

              {canEdit &&
                conditionedStockCollapseMetaDataList[createConditionedStockCollapseMetaDataListIndex(monthYearString)]
                  .isUpdateMode && (
                  <div className={classes.actionButtonContainer}>
                    <Button
                      onClick={() =>
                        handleClickCancelButton(createConditionedStockCollapseMetaDataListIndex(monthYearString))
                      }
                      startIcon={<CancelOutlinedIcon />}
                      variant="outlined"
                      className={classes.actionButton}
                    >
                      {t('cancel')}
                    </Button>
                    <Button
                      endIcon={<ValidateIcon />}
                      variant="outlined"
                      color={'primary'}
                      type="submit"
                      form={createConditionedStockCollapseMetaDataListIndex(monthYearString)}
                      className={classes.actionButton}
                    >
                      {t('save')}
                    </Button>
                  </div>
                )}

              {canEdit &&
                !conditionedStockCollapseMetaDataList[createConditionedStockCollapseMetaDataListIndex(monthYearString)]
                  .isUpdateMode &&
                !conditionedStockCollapseMetaDataList[createConditionedStockCollapseMetaDataListIndex(monthYearString)]
                  .isBeforeOrSameMonthThanLastClosure &&
                !conditionedStockCollapseMetaDataList[createConditionedStockCollapseMetaDataListIndex(monthYearString)]
                  .isTwoMonthsLaterDate && (
                  <>
                    <div className={classes.updateButtonContainer}>
                      <Button
                        onClick={() =>
                          handleClickUpdateButton(createConditionedStockCollapseMetaDataListIndex(monthYearString))
                        }
                        variant="outlined"
                        color="default"
                      >
                        {t('inventoryForm.button.update')}
                      </Button>
                    </div>
                  </>
                )}
            </>
          }
        >
          <Typography className={classes.typography}>
            <span className={classes.element}>{monthYearString.monthInLetter}</span>
            <span className={classes.element}>{monthYearString.year}</span>
          </Typography>
        </CollapsableCard>
      ))}
    </>
  );
};
export default ConditionedStock;
